// WP-1.18 (SR-3, S-10): per-record attachment gallery — thumbnails for images, a labelled
// link for PDFs, an upload input, and per-item delete. Content is served auth-gated and
// same-origin, so the browser attaches the session cookie to <img>/<a> automatically.
import { FileText, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import {
  attachmentContentUrl,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '../lib/attachments';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // readAsDataURL → "data:<type>;base64,<payload>"; strip the prefix.
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => {
      reject(new Error('Could not read the file'));
    };
    reader.readAsDataURL(file);
  });
}

export function AttachmentsGallery({
  entityType,
  entityId,
}: {
  entityType: string;
  entityId: string;
}): React.JSX.Element {
  const { data: attachments } = useAttachments(entityType, entityId);
  const upload = useUploadAttachment();
  const remove = useDeleteAttachment();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File): Promise<void> => {
    setError(null);
    try {
      const dataBase64 = await fileToBase64(file);
      upload.mutate(
        { entityType, entityId, filename: file.name, contentType: file.type, dataBase64 },
        {
          onError: (e) => {
            setError(e instanceof ApiError ? e.message : 'Upload failed');
          },
        },
      );
    } catch {
      setError('Could not read the file');
    }
  };

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-ink">Attachments</p>
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-primary hover:underline"
          onClick={() => {
            inputRef.current?.click();
          }}
        >
          <Upload size={14} /> Add
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          aria-label="Upload attachment"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file !== undefined) void onFile(file);
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
      </div>

      {(attachments === undefined || attachments.length === 0) && (
        <p className="text-xs text-muted">No attachments yet.</p>
      )}

      {attachments !== undefined && attachments.length > 0 && (
        <ul className="flex flex-wrap gap-3">
          {attachments.map((att) => (
            <li key={att.id} className="relative">
              <a
                href={attachmentContentUrl(att.id)}
                target="_blank"
                rel="noreferrer"
                title={att.filename}
              >
                {att.contentType.startsWith('image/') ? (
                  <img
                    src={attachmentContentUrl(att.id)}
                    alt={att.filename}
                    className="h-20 w-20 rounded-[var(--radius-control)] border border-border object-cover"
                  />
                ) : (
                  <span className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-[var(--radius-control)] border border-border text-muted">
                    <FileText size={20} />
                    <span className="max-w-[72px] truncate px-1 text-[10px]">{att.filename}</span>
                  </span>
                )}
              </a>
              <button
                type="button"
                aria-label={`Delete ${att.filename}`}
                className="absolute -right-2 -top-2 rounded-full border border-border bg-surface p-1 text-muted hover:text-danger"
                disabled={remove.isPending}
                onClick={() => {
                  remove.mutate(att.id);
                }}
              >
                <Trash2 size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error !== null && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
}
